import './table.css';
import { isAlwaysShownColumn } from '@src/util/viewColumns/viewColumns';
import React, { useEffect, useRef, useState } from 'react';
import { Layout, theme, Table, Input, Avatar, Space, Button, Tooltip, Popover, Pagination } from 'antd';
import { Loader } from '../loader';
import { useQuery, useMutation } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckToSlot, faEnvelope, faMinus, faPencil, faPhone, faSearch, faTrashCan, faTrashCanArrowUp } from '@fortawesome/free-solid-svg-icons';
import { objectType } from "@src/util/types/object.types";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { updateBranchView } from '@src/util/mutation/branchView.mutation';
import { formatDate } from '@src/util/formatDate';

const DraggableHeaderCell = ({ index, isFixed, children, ...restProps }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: index ?? 'fixed',
    disabled: isFixed,
  });
  const style = {
    ...restProps.style,
    ...(!isFixed && {
      transform: CSS.Translate.toString(transform),
      transition,
      cursor: isDragging ? 'grabbing' : 'grab',
      opacity: isDragging ? 0.5 : 1,
      position: 'relative',
      zIndex: isDragging ? 1 : 0,
    }),
  };
  return (
    <th
      {...restProps}
      ref={isFixed ? undefined : setNodeRef}
      style={style}
      {...(isFixed ? {} : { ...attributes, ...listeners })}
    >
      {children}
    </th>
  );
};

  
import { Resizable } from 'react-resizable';
import { useDispatch } from 'react-redux';
import { refreshBranchGrid } from '@src/middleware/redux/reducers/branch.reducer';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { setTableIsExporting, setTriggerExport } from '@src/middleware/redux/reducers/tableSelection.reducer';
import { EditPropertiesModal } from './modal/editproperty.modal';
import { PROPERTYWITHFILTER } from '@src/util/query/properties.query';
import { SearchOutlined, UserOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { findPayDate, findPayCurrency, formatPayRate } from "@src/util/findPayDate/findPayDate";
import { currencySymbol } from "@src/util/currency/currency";
import { useDefaultCurrency } from "@src/hooks/useDefaultCurrency";
import dayjs from 'dayjs';


const { Header, Content, Footer } = Layout;

export const DataTable = ({
  header, data, loading, title, objectData:propertyObject, selectionAllowed=true,
  viewRefetch, view, detailpage, handelBulkUpdateSave, handelArchive, archiveLabel="Archive", handelDelete, clearSelection, detailbtn="Preview", detailUrl="",
  totalCount, currentPage, totalPages, setPageSize, setPage, pageSize, showSizeChanger=true, setSearch, search,
  onExportAll, onSortChange, objectType: propertyObjectType
}) => {
    // pay rates saved before currencies were recorded are in the tenant's default currency
    const defaultCurrency = useDefaultCurrency();
  

  
  const [dynamicColumn, setDynamicColumn] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [objectData, setObjectData] = useState(propertyObject);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [updateColumnView] = useMutation(updateBranchView);

  const handleColumnDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = Number(active.id);
    const newIndex = Number(over.id);
    const newColumns = arrayMove(dynamicColumn, oldIndex, newIndex);
    setDynamicColumn(newColumns);

    const selectedViewId = sessionStorage.getItem('selectedViewId');
    if (!selectedViewId) return;

    const newViewFields = newColumns
      .filter(col => !isAlwaysShownColumn(col.originalObj))
      .map((col, i) => {
        const propId = col.originalObj?.propertyDetail?._id;
        const existingVF = Array.isArray(view) ? view.find(vf => vf?._id === propId) : null;
        return {
          _id: propId,
          label: col.title,
          isMandatory: existingVF?.isMandatory || false,
          isLocalDeleted: 0,
          order: i + 1,
        };
      });

    sessionStorage.setItem('reorderedItems', JSON.stringify(newViewFields));
    await updateColumnView({
      variables: { input: { _id: selectedViewId, viewFields: newViewFields } },
    });
  };


  useEffect(()=>{
    if(propertyObject?.length>0){
      setObjectData([...propertyObject].sort((a, b) => a.order - b.order));
    }
  },[propertyObject])

  const { loading:propertyListLoading, data:propertyDataList } = useQuery(PROPERTYWITHFILTER,{
    variables:{
        input:{fields:[
          {field:'groupId', value: ''},
          {field:'fieldType', value: '' },
          {field: 'objectType', value: propertyObjectType || title },
        ]}
    },
    skip: !(propertyObjectType || title),
    fetchPolicy: 'network-only'
  });


  const [hoveredCell, setHoveredCell] = useState({ rowIndex: null, columnKey: null });

  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef(null);
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
    setSearch(selectedKeys[0]);
  };
  const handleReset = clearFilters => {
    clearFilters();
    setSearchText('');
  };


  const getColumnSearchProps = (dataIndex, label) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={e => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${label}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => { handleReset(clearFilters); handleSearch([], confirm, dataIndex) }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
    onFilter: (value, record) => {
      
      
      if(typeof record[dataIndex] === "object"){
        
        return record[dataIndex]?.props?.children?.toString().toLowerCase().startsWith(value?.toLowerCase());

      }
      else if(record[dataIndex] && !record[dataIndex]?.includes('-')){
        
        return record[dataIndex]?.toString().toLowerCase().startsWith(value?.toLowerCase())

      }
      else if(record[dataIndex] && record[dataIndex]?.includes('-')){
        
        const updatedValue = value.replaceAll("/", "");
        const columnValue = record[dataIndex]?.toString().split('-').reverse().join('');

        return columnValue?.startsWith(updatedValue)

      }
      
    },
    filterDropdownProps: {
      onOpenChange(open) {
        if (open) {
          setTimeout(() => {
            var _a;
            return (_a = searchInput.current) === null || _a === void 0 ? void 0 : _a.select();
          }, 100);
        }
      },
    },
  });


  useEffect(()=>{
    if(objectData && objectData?.length>0){

      if(objectData?.length>0 && view && propertyDataList?.getPropertywithFilters?.length>0){
        

        // the view's own columns, after the record's name - not every field the form requires
        const mandatoryField = objectData?.filter((prop)=> isAlwaysShownColumn(prop));
        const shown = new Set(mandatoryField.map((prop)=> String(prop?.propertyDetail?._id ?? prop?.propertyId)));
        const additionalProp = view?.map((viewProp)=> {
          const isExist = propertyDataList?.getPropertywithFilters?.find((prop)=>viewProp?._id==prop?._id);
          if(!isExist && viewProp?.fieldType === 'payWidget'){
            return {propertyDetail: viewProp};
          }
          return {propertyDetail: isExist};
        }).filter((prop)=>{
          // once each (a view can list a column twice, or list the name again)
          const id = String(prop?.propertyDetail?._id ?? "");
          if(!prop?.propertyDetail || shown.has(id)) return false;
          shown.add(id);
          return true;
        });
        const updateView = [...mandatoryField, ...additionalProp]?.sort((a,b)=>a?.order - b?.order);


        const col = updateView?.filter(view=> view?.propertyDetail).map((prop)=>{

            const propName = prop?.propertyDetail?.label?.replaceAll(" ","").toLowerCase();       
            const searchProp = title===objectType?.Timeline? {} : getColumnSearchProps(propName, prop?.propertyDetail?.label);
            
            return {
              originalObj: prop,
              title: prop?.propertyDetail?.label,
              dataIndex: propName,
              key: propName,
              width: Math.max(140, (prop?.propertyDetail?.label?.length || 0) * 9 + 32),
              onHeaderCell: () => ({ style: { whiteSpace: 'nowrap', minWidth: Math.max(140, (prop?.propertyDetail?.label?.length || 0) * 9 + 32) } }),
              onCell: (_, rowIndex) => ({
                style: { minWidth: Math.max(140, (prop?.propertyDetail?.label?.length || 0) * 9 + 32) },
                onMouseEnter: () => {
                  if(propName=="sitename" && title===objectType?.Timeline){
                    setHoveredCell({ rowIndex, columnKey: 'sitename' });
                  }
                },
                onMouseLeave: () => {
                  setHoveredCell({ rowIndex: null, columnKey: null });
                },
              }),
              
              render: (_, record, rowIndex) => {
                
                const showActions =  title==objectType?.Timeline?
                                      propName=="sitename" && title===objectType?.Timeline && hoveredCell.rowIndex === rowIndex && hoveredCell.columnKey === 'sitename'  &&  selectedRowKeys?.length===0
                                      :
                                      sessionStorage.getItem('hoverItem') == record.key  && prop?.propertyDetail?.label == updateView[0]?.propertyDetail?.label &&  selectedRowKeys?.length===0;
                                      
                
                if(propName=="phonenumber"
                ){
                  return(
                    <a style={{textDecoration:'underline', whiteSpace:'nowrap'}} href={"tel:"+record[prop?.propertyDetail?.label.replaceAll(" ","").toLowerCase()]}>
                      {record[prop?.propertyDetail?.label.replaceAll(" ","").toLowerCase()]}
                    </a>
                  )
                }
                else if(propName=="email"
                ){
                  return(
                    <a style={{textDecoration:'underline', whiteSpace:'nowrap'}} target='_blank' href={"mailto:"+record[prop?.propertyDetail?.label.replaceAll(" ","").toLowerCase()]}>
                      {record[prop?.propertyDetail?.label.replaceAll(" ","").toLowerCase()]}
                    </a>
                  )
                }
                else{

                  return (          
                    <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', whiteSpace:'nowrap'}}>

                      <div style={{display:'flex', alignItems:'center', gap:'9px', whiteSpace:'nowrap'}}>
                      
                        {detailpage?.includes("employee") && prop?.propertyDetail?.label.replaceAll(" ","").toLowerCase()=="firstname" &&
                          <>
                          {record?.imageURL?
                            <Avatar size={30}  src={record?.imageURL}/> 
                          
                          :
                          // <div className="employeeProfile">
                            <Avatar size={30} style={{fontSize:'10px'}}><UserOutlined /></Avatar>
                          // </div>
                          }
                            
                          </>
                        }

                        {
                          prop?.propertyDetail?.fieldType=="date" ?
                            <div style={{whiteSpace:'nowrap'}}>
                              {formatDate(record[propName])}
                            </div>
                          :
                          detailpage?.length>0?

                            <a
                              href={detailpage?.length>0? ('/user/'+detailpage+record.key) : ""}
                              className={showActions? 'prev-btn' : null}
                              style={{whiteSpace:'nowrap'}}
                              onClick={saveGridState}
                              >
                              {String(record[propName] ?? '').length > 36
                                ? <Popover content={record[propName]}>{String(record[propName]).slice(0, 36) + '…'}</Popover>
                                : record[propName]
                              }
                            </a>
                          :

                          <div
                            onClick={detailpage?.length>0? ()=> ('/user/'+detailpage+record.key) : ()=>{}}
                            className={showActions? 'prev-btn' : null}
                            style={{whiteSpace:'nowrap'}}
                          >
                            {String(record[propName] ?? '').length > 36
                              ? <Tooltip title={record[propName]}>{String(record[propName]).slice(0, 36) + '…'}</Tooltip>
                              : record[propName]
                            }
                          </div>

                        }

                          
                      </div>
      
                    
                      {((title==objectType?.Timeline && propName!=="sitename") || (title==objectType?.AttendanceGap) ) ?
                        null:
                        
                        title=="Employee" && prop?.propertyDetail?.label.replaceAll(" ","").toLowerCase()=="firstname" ?

                        <div style={{marginLeft: 16, fontSize: 10, color: '#9ca3af', display:'flex', flexDirection:'row', flexShrink:0, alignItems:'center', gap:'15px'}}>

                            <Popover
                                content={ (record?.mobileno) || "no number found"}>
                                <FontAwesomeIcon icon={faPhone} style={{cursor:'pointer'}} 
                                title={
                                    (record?.mobileno) || "no number found"
                                }
                                />
                            </Popover>

                            <a

                                href={
                                (record?.mobileno)?
                                `https://wa.me/${
                                (record?.mobileno)
                                    ?.toString()
                                    .replaceAll(/\D/g, "")
                                }` : ""}
                                target="_blank"
                                rel="noreferrer"
                            >

                                <WhatsAppOutlined style={{fontSize:12}}/>
                            </a>

                            <Popover
                                content={
                                    
                                (record?.email) || "no email found"
                                
                                }
                            >
                                <FontAwesomeIcon icon={faEnvelope} 
                                  title={
                                    record?.email
                                  }
                                />
                            </Popover>

                        </div>

                        :

                        <button className={"sm-btn"} style={showActions?{visibility: 'visible'}:{visibility: 'hidden'}} type="link" onClick={()=>{
                          if(title==objectType?.Timeline){
                            saveGridState();
                            history("/user/schedule",{
                                state: {timelineScheduleId: record?.call?.scheduleId, timelineSelectedSiteId: record?.call?.siteId, timelineSelectedDate: record?.call?.scheduleDetail[0]?.date}
                            });

                          }
                          else{
                            saveGridState();
                            history('/user/'+detailpage+record?.key+detailUrl)
                          }
                        }}>
                          {detailbtn}
                        </button>
                      }
                    
                    </div>
                  );

                }
              },

              sorter: title === objectType?.Timeline ? false : true,

              // ...searchProp,

            }
          
          
        })||[];
        setDynamicColumn([...col]);
        dispatch(refreshBranchGrid(false));

      }
      else if(objectData?.length>0 && view==false){

        const col = objectData?.filter(view=> view?.propertyDetail).map((prop)=>{  
            return {
              originalObj: prop,
              title: prop.propertyDetail.label,
              dataIndex: prop.propertyDetail.label.replaceAll(" ","").toLowerCase(),
              key: prop.propertyDetail.label.replaceAll(" ","").toLowerCase(),
              width: Math.max(140, (prop.propertyDetail.label?.length || 0) * 9 + 32),
              onHeaderCell: () => ({ style: { whiteSpace: 'nowrap', minWidth: Math.max(140, (prop.propertyDetail.label?.length || 0) * 9 + 32) } }),
              onCell: () => ({ style: { minWidth: Math.max(140, (prop.propertyDetail.label?.length || 0) * 9 + 32) } }),

              render: (_, record) => {
                const showActions = sessionStorage.getItem('hoverItem') == record.key && prop.propertyDetail.label == objectData[0].propertyDetail.label &&  selectedRowKeys?.length===0;
                if(prop.propertyDetail.label.replaceAll(" ","").toLowerCase()=="phonenumber"
                ){
                  return(
                    <a style={{textDecoration:'underline'}} href={"tel:"+record[prop.propertyDetail.label.replaceAll(" ","").toLowerCase()]}>
                      {record[prop.propertyDetail.label.replaceAll(" ","").toLowerCase()]}
                    </a>
                  )
                }
                else if(prop.propertyDetail.label.replaceAll(" ","").toLowerCase()=="email"
                ){
                  return(
                    <a style={{textDecoration:'underline'}} target='_blank' href={"mailto:"+record[prop.propertyDetail.label.replaceAll(" ","").toLowerCase()]}>
                      {record[prop.propertyDetail.label.replaceAll(" ","").toLowerCase()]}
                    </a>
                  )
                }
                else{
                return (          
                  <div style={{display:'flex', alignItems:'center', justifyContent:'space-between',}}>
                    
                    <div
                      onClick={()=>{ saveGridState(); history('/user/'+detailpage+record.key); }}
                      className={showActions? 'prev-btn' : null}
                      style={{whiteSpace:'nowrap', ...(prop.propertyDetail.label.replaceAll(" ","").toLowerCase()=="firstname" || prop.propertyDetail.label.replaceAll(" ","").toLowerCase()=="branchname" || prop.propertyDetail.label.replaceAll(" ","").toLowerCase()=="sitegroupname"?{color:'#0091ae'}:{})}}
                    >
                      {(() => { const v = record[prop.propertyDetail.label.replaceAll(" ","").toLowerCase()]; return String(v ?? '').length > 36 ? <Tooltip title={v}>{String(v).slice(0,36)+'…'}</Tooltip> : v; })()}
                    </div>
                      
    
                  

                    <button className={"sm-btn"} style={showActions?{visibility: 'visible'}:{visibility: 'hidden'}} type="link" onClick={()=>{ saveGridState(); history('/user/'+detailpage+record.key); }}>
                      Preview
                    </button>
                  
                </div>
                );
                }
              },
            }
          // }
        })||[];

        setDynamicColumn([...col]);
      }

    }
  }, [objectData, view, propertyDataList?.getPropertywithFilters, hoveredCell]);


  useEffect(()=>{
    if(clearSelection){
      setSelectedRowKeys([]);
    }
  },[clearSelection]);


  const dispatch = useDispatch();
  const branchReducer = useSelector(state => state.branchReducer);

  useEffect(()=>{
    if(branchReducer?.refreshGrid){

      viewRefetch();
    }
  },[branchReducer?.refreshGrid]);
  
  const [hoveredRow, setHoveredRow] = useState(null);

  const rowClassName = (record) => {
    return record.key === hoveredRow ? 'hovered-row' : '';
  };

  
  
  const handleRowMouseEnter = (record) => {
    setHoveredRow(record.key);
    sessionStorage.setItem('hoverItem', record.key);
  };


  const handleRowMouseLeave = () => {
    setHoveredRow(null);
    // setMoreoption(false);
    sessionStorage.removeItem('hoverItem');

  };

  const history = useNavigate();
  const location = useLocation();
  const storageKey = `grid_state_${title || location.pathname}`;
  const hasMountedRef = useRef(false);
  const restoredSearchRef = useRef('');

  const saveGridState = () => {
    sessionStorage.setItem(storageKey, JSON.stringify({
      page: Number(currentPage),
      size: Number(pageSize),
      searchVal: localSearch,
    }));
  };


  const transformRecords = (records) =>
    records?.map((key) => {
      const {metadata, ...rest} = key;
      if(Array.isArray(metadata?.payrate) || Array.isArray(metadata?.overtimepayrate)){
        return {key:key?._id ,...metadata, payrate: formatPayRate(metadata?.payrate, defaultCurrency), overtimepayrate: (metadata?.overtimepayrate), ...rest};
      }else{
        return {key:key?._id ,...metadata, ...rest};
      }
    }) || [];

  const generateCSV = (records) => {
    const columns = dynamicColumn.filter(col => col.dataIndex && col.title);
    const headers = columns.map(col => col.title);
    const rows = records.map(record =>
      columns.map(col => {
        const val = record[col.dataIndex];
        if (val === null || val === undefined) return '';
        if (typeof val === 'object') return '';
        const str = String(val);
        return str.includes(',') || str.includes('"') || str.includes('\n')
          ? `"${str.replace(/"/g, '""')}"`
          : str;
      })
    );
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const now = dayjs();
    link.download = `${title || 'export'}_${now.format('YYYY-MM-DD_HH-mm-ss')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = async () => {
    if (!dynamicColumn?.length) return;
    if (onExportAll) {
      dispatch(setTableIsExporting(true));
      try {
        const allRawData = await onExportAll();
        generateCSV(transformRecords(allRawData));
      } finally {
        dispatch(setTableIsExporting(false));
      }
    } else {
      if (!dataSource?.length) return;
      generateCSV(dataSource);
    }
  };

  const [dataSource, setDataSource] = useState([]);

  useEffect(()=>{
    setDataSource(transformRecords(data));
  },[data]);

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const tableSelectionState = useSelector(state => state.tableSelectionReducer);

  useEffect(()=>{
    if(!tableSelectionState?.triggerExport) return;
    dispatch(setTriggerExport(false));
    handleExportCSV();
  },[tableSelectionState?.triggerExport]);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };


  // normal row selection
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };


  const [propertyModal, setPropertyModal] = useState(false);

  const getPageSizeOptions = (maxPageSize) => {
    let page = pageSize;
    const options=[];
    for (let i = 1; i <= maxPageSize; i=i+1) {
      if(page<=100){
        options.push(page.toString());
        page = page + pageSize;
      }else{
        return;
      }
    }
  
    return options;
  };

  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSizeOptions, setPageSizeOptions] = useState([]);

  useEffect(()=>{
      setTotalRecords(totalCount);
      setPageSizeOptions(getPageSizeOptions(Math.ceil(totalCount/20)));
  },[totalCount, currentPage, totalPages]);


  const [localSearch, setLocalSearch] = useState("");
  const debouncedSearch = useDebounce(localSearch, 500); // 500ms delay

  // Trigger search — reset page to 1 on user-driven search changes.
  // Initial mount run and restored-search run skip the page reset.
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      setSearch(debouncedSearch);
      return;
    }
    if (restoredSearchRef.current !== '' && debouncedSearch === restoredSearchRef.current) {
      restoredSearchRef.current = '';
      setSearch(debouncedSearch);
      return;
    }
    setSearch(debouncedSearch);
    setPage(1);
  }, [debouncedSearch]);

  // Restore pagination state when returning from a detail page.
  // Uses setTimeout to defer after all parent components' initial effects have run
  // (parent effects run after child effects, and they call setPage(1) on [search] mount).
  useEffect(() => {
    const saved = sessionStorage.getItem(storageKey);
    if (!saved) return;
    sessionStorage.removeItem(storageKey);
    let timer;
    try {
      const { page, size, searchVal } = JSON.parse(saved);
      timer = setTimeout(() => {
        if (size) setPageSize(size);
        if (page) setPage(page);
        if (searchVal !== undefined) {
          restoredSearchRef.current = searchVal;
          setLocalSearch(searchVal);
        }
      }, 0);
    } catch (e) { /* ignore corrupt storage */ }
    return () => clearTimeout(timer);
  }, []);


  return (
    <Layout className='bg-white all-module-grid'>
      <Content className="site-layout" style={{ padding: '0 42px' }}>
        <div style={{ padding: 5, background: colorBgContainer }} className='allModule-grid'>

            {/* {loading || dynamicColumn?.length<1 || propertyListLoading ?
            <Loader/>
            : */}

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleColumnDragEnd}>
            <SortableContext items={dynamicColumn.map((_, i) => i)} strategy={horizontalListSortingStrategy}>
            <Table
  rowSelection={selectionAllowed ? rowSelection : null}
  columns={dynamicColumn.map((col, index) => ({
    ...col,
    onHeaderCell: (column) => ({
      ...(col.onHeaderCell ? col.onHeaderCell(column) : {}),
      index,
      isFixed: isAlwaysShownColumn(col.originalObj),
    }),
  }))}

  components={{
    header: {
      cell: DraggableHeaderCell,
    },
  }}

  pagination={false}

  summary={() =>
    selectedRowKeys?.length > 0 ? (
      <Table.Summary fixed="top">
        <Table.Summary.Row className="selection-action-row">
          <Table.Summary.Cell colSpan={dynamicColumn.length + 1}>
            <div className="selection-action-row-cell">
              
              <small >
                {selectedRowKeys.length} selected
              </small>

              {selectedRowKeys.length === dataSource?.length ? (
                <div
                  className="selection-action-item"
                  onClick={() => setSelectedRowKeys([])}
                >
                  <FontAwesomeIcon icon={faMinus} style={{ marginRight: "5px" }} />
                  <span>Unselect All Records</span>
                </div>
              ) : (
                <div
                  className="selection-action-item"
                  onClick={() =>
                    setSelectedRowKeys(dataSource?.map((d) => d?.key))
                  }
                >
                  <FontAwesomeIcon icon={faCheckToSlot} style={{ marginRight: "5px" }} />
                  <span>Select All Records</span>
                </div>
              )}

              <div
                className="selection-action-item"
                onClick={() => setPropertyModal(true)}
              >
                <FontAwesomeIcon icon={faPencil} style={{ marginRight: "5px" }} />
                <span>Edit</span>
              </div>

              {handelArchive && (
                <div
                  className="selection-action-item"
                  onClick={() => handelArchive(selectedRowKeys)}
                >
                  <FontAwesomeIcon icon={faTrashCanArrowUp} style={{ marginRight: "5px" }} />
                  <span>{archiveLabel}</span>
                </div>
              )}

              {/* permanent delete - only offered to users given the module's Delete permission */}
              {handelDelete && (
                <div
                  className="selection-action-item selection-action-danger"
                  onClick={() => handelDelete(selectedRowKeys)}
                >
                  <FontAwesomeIcon icon={faTrashCan} style={{ marginRight: "5px" }} />
                  <span>Delete</span>
                </div>
              )}
            </div>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    ) : null
  }

  sticky
  scroll={{
    x:
      !dataSource?.length && dynamicColumn?.length
        ? dynamicColumn.reduce((sum, col) => sum + (col.width || 140), 0)
        : "max-content",
  }}

  dataSource={dataSource}
  loading={loading || dynamicColumn?.length < 1 || propertyListLoading}
  rowClassName={rowClassName}
  className="grid-curvTable"
  locale={{ emptyText: "No record found" }}

  onChange={(_, __, sorter, extra) => {
    if (onSortChange && extra?.action === "sort") {
      const { field, order } = Array.isArray(sorter) ? sorter[0] : sorter;
      onSortChange(order ? field : null, order || null);
    }
  }}

  onRow={(record) => ({
    onMouseEnter: () => handleRowMouseEnter(record),
    onMouseLeave: () => handleRowMouseLeave(),
  })}
/>
            </SortableContext>
            </DndContext>

            <div className='table-pagination-bar'>
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={totalRecords}
                showSizeChanger={showSizeChanger}
                pageSizeOptions={pageSizeOptions}
                onChange={(page, size) => {
                  setPage(page);
                  setPageSize(size);
                }}
              />
            </div>

        </div>
{/* bulk edit properties modal */}
{propertyModal?
        <EditPropertiesModal
          visible={propertyModal}
          onClose={()=>setPropertyModal(!propertyModal)}
          record={selectedRowKeys}
          clearSelection = {setSelectedRowKeys}
          dynamicColumn={dynamicColumn}
          handelBulkUpdateSave={handelBulkUpdateSave}
        />
:null}
      
      </Content>
      
    </Layout>
  );
};

const ResizableTitle = ({ title, width, onResize, ...restProps }) => {
  return (
    <Resizable
      width={width}
      height={0}
      axis="x"
      handleSize={[10, 10]} // Size of the resize handle
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps}>
        {title}
      </th>
    </Resizable>
  );
};


export function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);  // Clear timeout on value change
    };
  }, [value, delay]);

  return debouncedValue;
}
